import { consumer } from '@tock/coretypes';
import { filter, map, size, slice, sortBy } from 'es-toolkit/compat';
import clsx from 'clsx';

import {
  BusinessEntity,
  ConsumerPurchaseSummary,
  PatronEntity,
  TicketTypeEntity,
} from 'generated/graphql';
import { StoreType } from '@tock/shared/utils/connect';
import { Typography } from 'omakase';
import { getUpcomingBookings } from 'utils/profileUtils';
import { patronHasSetUpProfileSelector } from 'selectors/patronSelectors';
import { purchaseSummariesSelector } from 'selectors/purchaseHistorySelector';
import { useSelector } from 'react-redux';
import Anchor from 'components/Anchor';
import ProfileSection from 'components/profile/ProfileSection';
import ReservationCard from 'components/profile/ReservationCard';

interface ConnectProps {
  abridgedPurchaseHistory: consumer.PurchaseSummary[];
  patron: StoreType['patron'];
  hasMoreReservations: boolean;
  hasSetUpProfile: boolean;
}

interface Props {
  isLoading: boolean;
}

const UpcomingReservationSection = ({ isLoading }: Props) => {
  const {
    patron: { isPurchaseSummariesInitialized },
    abridgedPurchaseHistory,
    hasMoreReservations,
    hasSetUpProfile,
  } = useSelector(mapStateToProps);

  const hideUpcomingReservations =
    isPurchaseSummariesInitialized && abridgedPurchaseHistory.length === 0;

  const showBorder = !hideUpcomingReservations && !hasSetUpProfile;

  const consumerPurchases: ConsumerPurchaseSummary[] = map(abridgedPurchaseHistory, (purchase) => {
    return {
      business: purchase.business as BusinessEntity,
      city: purchase.city as string,
      country: purchase.country as string,
      dinerPatron: purchase.dinerPatron?.[0] as unknown as PatronEntity,
      firstTransferredTo: purchase.firstTransferredTo as PatronEntity,
      id: purchase.id,
      cancelledOrRefunded: purchase.cancelledOrRefunded,
      eligibleForFeedback: purchase.isEligibleForFeedback,
      ownerPatron: purchase.ownerPatron as PatronEntity,
      ticketCount: purchase.ticketCount,
      ticketDateTime: purchase.ticketDateTime,
      ticketType: purchase.ticketType as TicketTypeEntity,
      cancellationPolicyDescription: [],
      seatingAreaName: '',
      confirmationText: '',
      confirmationCode: '',
    };
  });

  if (hideUpcomingReservations) {
    return null;
  }
  return (
    <ProfileSection
      name="Your upcoming reservations"
      action={
        hasMoreReservations ? (
          <Anchor className="Profile-sectionTitleLink" href="/profile/reservations">
            <Typography color="inherit" component="span" variant="body02Strong">
              See all
            </Typography>
          </Anchor>
        ) : undefined
      }
      sectionStyle={clsx('Profile-reservationSection', { showBorder })}
      wrapperStyle="Profile-reservationSectionWrapper"
      isLoading={isLoading}
    >
      <div className="ReservationPreviewCardList">
        {consumerPurchases &&
          map(consumerPurchases, (purchase) => (
            <ReservationCard reservation={purchase} key={purchase.id} />
          ))}
      </div>
    </ProfileSection>
  );
};

const mapStateToProps = (state: StoreType): ConnectProps => {
  const { app, patron } = state;
  const futureReservationsIncludingNonDiner = getUpcomingBookings(
    purchaseSummariesSelector(state),
    app.patron ? app.patron.email : ''
  );
  const futureReservations = filter(
    futureReservationsIncludingNonDiner,
    (purchase) =>
      !purchase.ownerPatron ||
      !purchase.dinerPatron[0] ||
      purchase.ownerPatron.email === purchase.dinerPatron[0].email
  );
  const sortedReservations = sortBy(futureReservations, (purchase) => purchase.ticketDateTime);
  return {
    patron,
    abridgedPurchaseHistory: slice(sortedReservations, 0, 3),
    hasMoreReservations: size(sortedReservations) > 3,
    hasSetUpProfile: patronHasSetUpProfileSelector(state),
  };
};

export default UpcomingReservationSection;
